import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { phases } from '../data/phases';

const { FiCheckCircle, FiPlus, FiExternalLink, FiPrinter, FiDownload } = FiIcons;

function Results({ phase, onReset }) {
  const phaseData = phases[phase];

  if (!phaseData) {
    return <div>Phase data not found</div>;
  }

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    const content = `
READING PHASE SCREENER RESULTS

Phase: ${phaseData.title}

Description:
${phaseData.description}

Recommendations:
${phaseData.recommendations.map((rec, index) => `${index + 1}. ${rec.replace(/🟢\s*/, '')}`).join('\n')}

Resources:
${phaseData.resources.map((resource, index) => `${index + 1}. ${resource.title}: ${resource.description}\n   Link: ${resource.link}`).join('\n\n')}

Generated by: Reading Phase Screener | Thrive Literacy Corner
Website: https://thriveedservices.com
Date: ${new Date().toLocaleDateString()}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reading-phase-results-${phase}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <motion.div 
        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-brand-sage/20 p-8 print:shadow-none print:border-2 print:border-gray-300"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-block p-4 rounded-full bg-gradient-to-br from-brand-teal to-brand-dark-blue mb-6 print:bg-gray-200 print:text-black"
          >
            <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-white print:text-black" />
          </motion.div>

          <motion.h2
            className="text-3xl font-abril text-brand-dark-blue mb-4 print:text-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {phaseData.title}
          </motion.h2>

          <motion.p
            className="text-lg text-gray-700 leading-relaxed print:text-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            {phaseData.description}
          </motion.p>
        </div>

        {/* Export/Print Buttons - Hidden in print */}
        <motion.div 
          className="flex justify-center gap-4 mb-8 print:hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.5 }}
        >
          <motion.button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 text-brand-dark-blue font-semibold py-3 px-6 rounded-xl shadow-lg border border-brand-sage/30 transition-all duration-200 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-brand-teal/30"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPrinter} className="h-5 w-5" />
            Print Results
          </motion.button>

          <motion.button
            onClick={handleExport}
            className="flex items-center gap-2 bg-white hover:bg-gray-50 text-brand-dark-blue font-semibold py-3 px-6 rounded-xl shadow-lg border border-brand-sage/30 transition-all duration-200 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-brand-teal/30"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiDownload} className="h-5 w-5" />
            Export as Text
          </motion.button>
        </motion.div>

        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <h3 className="text-2xl font-abril text-brand-dark-blue mb-6 print:text-black">Start here:</h3>
          <div className="space-y-4">
            {phaseData.recommendations.map((recommendation, index) => (
              <motion.div
                key={index}
                className="flex items-start gap-3 p-4 bg-gradient-to-r from-brand-sage-light to-brand-peach-light rounded-xl border border-brand-sage/30 print:bg-gray-50 print:border-gray-300"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
              >
                <span className="text-2xl print:text-lg">{recommendation.split(' ')[0]}</span>
                <span className="text-gray-800 font-medium leading-relaxed print:text-black">
                  {recommendation.substring(recommendation.indexOf(' ') + 1)}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="bg-gradient-to-r from-brand-peach-light to-brand-mauve-light p-6 rounded-xl border border-brand-mauve/30 mb-8 print:bg-gray-50 print:border-gray-300"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-brand-teal print:text-black" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-brand-dark-blue mb-2 print:text-black">
                Ready to implement these recommendations?
              </h4>
              <p className="text-gray-700 leading-relaxed print:text-black">
                Use our{' '}
                <a
                  href="https://shop.thriveedservices.com/products/science-of-reading-small-group-lesson-plan-templates"
                  className="text-brand-teal hover:text-brand-teal-dark font-semibold underline transition-colors print:text-black"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Science of Reading Small Group Lesson Plan Templates
                </a>
                {' '}to organize your instruction.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="border-t border-brand-sage/30 pt-6 text-center print:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <motion.button
            onClick={onReset}
            className="bg-gradient-to-r from-brand-teal to-brand-dark-blue hover:from-brand-teal-dark hover:to-brand-dark-blue text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl flex items-center gap-2 mx-auto focus:outline-none focus:ring-4 focus:ring-brand-teal/30"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPlus} className="h-5 w-5" />
            Screen Another Student
          </motion.button>
        </motion.div>
      </motion.div>

      <motion.div
        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-brand-sage/20 p-8 print:shadow-none print:border-2 print:border-gray-300 print:break-inside-avoid"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <h3 className="text-2xl font-abril text-brand-dark-blue mb-6 print:text-black">Resources for this phase</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-1">
          {phaseData.resources.map((resource, index) => (
            <motion.div
              key={index}
              className="bg-gradient-to-br from-white to-brand-sage-light p-6 rounded-xl border border-brand-sage/30 hover:border-brand-teal/50 transition-all duration-200 hover:shadow-md group print:bg-gray-50 print:border-gray-300 print:shadow-none print:hover:shadow-none"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="font-abril text-brand-dark-blue mb-2 text-lg print:text-black">
                {resource.title}
              </h4>
              <p className="text-gray-700 mb-4 leading-relaxed text-sm print:text-black">
                {resource.description}
              </p>
              <a
                href={resource.link}
                className="text-brand-teal hover:text-brand-teal-dark font-semibold text-sm inline-flex items-center gap-1 transition-colors group-hover:gap-2 print:text-black"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
                <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
              </a>
              <div className="hidden print:block mt-2 text-xs text-gray-600">
                {resource.link}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-8 pt-6 border-t border-brand-sage/30 text-center print:border-gray-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <p className="text-gray-700 mb-4 text-lg print:text-black">
            Find more teaching resources in our shop
          </p>
          <motion.a
            href="https://shop.thriveedservices.com/"
            className="bg-gradient-to-r from-brand-teal to-brand-dark-blue hover:from-brand-teal-dark hover:to-brand-dark-blue text-white font-semibold py-3 px-8 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl inline-block focus:outline-none focus:ring-4 focus:ring-brand-teal/30 print:bg-gray-200 print:text-black print:shadow-none"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Visit Thrive Literacy Corner
          </motion.a>
          <div className="hidden print:block mt-2 text-sm text-gray-600">
            https://shop.thriveedservices.com/
          </div>
        </motion.div>

        {/* Print footer */}
        <div className="hidden print:block mt-8 pt-6 border-t border-gray-300 text-center text-sm text-gray-600">
          <p>Generated by Reading Phase Screener | Thrive Literacy Corner</p>
          <p>Date: {new Date().toLocaleDateString()}</p>
          <p>Website: https://thriveedservices.com</p>
        </div>
      </motion.div>
    </div>
  );
}

export default Results;